import { BehaviorSubject } from 'rxjs';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  ViewChild,
  DoCheck,
  ElementRef,
  TrackByFunction,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

@Component({
    selector: 'mat-select-autocomplete',
    templateUrl: './select-autocomplete.component.html',
    styleUrls: ['./select-autocomplete.component.scss'],
    standalone: false
})
export class SelectAutocompleteComponent implements OnInit, OnChanges, DoCheck {
  @Input() selectPlaceholder = 'search...';
  @Input() placeholder: string;
  @Input() options: any[];
  @Input() disabled = false;
  @Input() display = 'display';
  @Input() value = 'value';
  @Input() formControl: UntypedFormControl = new UntypedFormControl();
  @Input() errorMsg = 'Field is required';
  @Input() showErrorMsg = false;
  @Input() selectedOptions: any[];
  @Input() multiple = true;
  @Input() selectAll = false;

  // New Options
  @Input() labelCount = 1;
  @Input() appearance: 'fill' | 'outline' = 'outline';

  // Bind Filter Input
  @Input()
  public filterInput: BehaviorSubject<string>;

  @Output()
  selectionChange: EventEmitter<any> = new EventEmitter();
  @Output()
  openedChange: EventEmitter<any> = new EventEmitter();
  @Output()
  clear: EventEmitter<any> = new EventEmitter();

  @ViewChild('selectElem') selectElem: { toggle: () => void };
  @ViewChild('textInput') textInput: ElementRef;

  searchInput: string = '';

  filteredOptions: Array<any> = [];
  selectedValue: Array<any> = [];
  selectAllChecked = false;
  displayString = '';
  constructor() {}

  ngOnInit(): void {
    this.filterItem('');
  }

  ngOnChanges() {
    if (this.disabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
    this.filteredOptions = this.options;
    if (this.selectedOptions) {
      this.selectedValue = this.selectedOptions;
    } else if (this.formControl.value) {
      this.selectedValue = this.formControl.value;
    }
  }

  ngDoCheck() {
    if (this.selectedValue && !this.selectedValue.length) {
      this.selectionChange.emit(this.selectedValue);
    }
  }

  getFormFieldAppearance() {
    return this.appearance;
  }

  toggleDropdown() {
    this.selectElem.toggle();
  }

  toggleSelectAll(val: { checked: any }) {
    if (val.checked) {
      this.filteredOptions.forEach((option) => {
        if (!this.selectedValue.includes(option[this.value])) {
          this.selectedValue = this.selectedValue.concat([option[this.value]]);
        }
      });
    } else {
      const filteredValues = this.getFilteredOptionsValues();
      this.selectedValue = this.selectedValue.filter(
        (item) => !filteredValues.includes(item)
      );
    }
    this.selectionChange.emit(this.selectedValue);
  }

  filterItem(value: string) {
    this.filterInput.next(value);
    this.filteredOptions = this.options.filter(
      (item) =>
        item[this.display].toLowerCase().indexOf(value.toLowerCase()) > -1
    );
    this.selectAllChecked = true;
    this.filteredOptions.forEach((item) => {
      if (
        this.selectedValue &&
        !this.selectedValue.includes(item[this.value])
      ) {
        this.selectAllChecked = false;
      }
    });
    if (!this.filteredOptions.length) {
      this.selectAllChecked = false;
    }
  }

  hideOption(option: any) {
    return !(this.filteredOptions.indexOf(option) > -1);
  }

  // Returns plain strings array of filtered values
  getFilteredOptionsValues() {
    const filteredValues: any[] = [];
    this.filteredOptions.forEach((option) => {
      filteredValues.push(option.value);
    });
    return filteredValues;
  }

  onDisplayString() {
    this.displayString = '';
    if (this.selectedValue && this.selectedValue.length) {
      let displayOption = [];
      if (this.multiple) {
        // Multi select display
        for (let i = 0; i < this.labelCount; i++) {
          displayOption[i] = this.options.filter(
            (option) => option[this.value] === this.selectedValue[i]
          )[0];
        }
        if (displayOption.length) {
          for (let i = 0; i < displayOption.length; i++) {
            if (displayOption[i] && displayOption[i][this.display]) {
              this.displayString += displayOption[i][this.display] + ',';
            }
          }
          this.displayString = this.displayString.slice(0, -1);
          if (
            this.selectedValue.length > 1 &&
            this.selectedValue.length > this.labelCount
          ) {
            this.displayString += ` (+${
              this.selectedValue.length - this.labelCount
            } others)`;
          }
        }
      } else {
        // Single select display
        displayOption = this.options.filter(
          (option) => option[this.value] === this.selectedValue
        );
        if (displayOption.length) {
          this.displayString = displayOption[0][this.display];
        }
      }
    }
    return this.displayString;
  }

  onSelectionChange(val: { value: any[] }) {
    const filteredValues = this.getFilteredOptionsValues();
    let count = 0;
    if (this.multiple) {
      this.selectedValue.filter((item) => {
        if (filteredValues.includes(item)) {
          count++;
        }
      });
      this.selectAllChecked = count === this.filteredOptions.length;
    }
    this.selectedValue = val.value;
    this.selectionChange.emit(this.selectedValue);
  }

  onOpenedChange(event: any) {
    this.openedChange.emit(event);
    setTimeout(() => {
      this.textInput.nativeElement.focus();
    }, 10);
  }

  public trackByFn(item: any) {
    return item.value as TrackByFunction<any>;
  }

  clearSearch() {
    this.filterItem('');
    this.searchInput = '';
    !this.multiple ? this.formControl.setValue(null) : null;
    this.clear.emit();
  }
}
